import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Image, ScrollView, StyleSheet, View } from 'react-native';
import { AppView } from 'design-system/AppView';
import { CardView } from 'design-system/CardView';
import ConnectionFailed from 'design-system/ConnectionFailed';
import { Indicator } from 'design-system/Indicator';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { rem, SCREEN_BOTTOM_PADDING } from 'design-system/values';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import Amplitude from 'utils/amplitude';
import { getStreaks } from '../actions';
import StreakBadgeCard from '../components/StreakBadgeCard';
import StreaksCalendar from '../components/StreaksCalendar';
import { selectStreakBadges, selectStreaksMonth, selectStreaksSummary, selectUserDayStreak, } from '../selectors';
import { dayWord, formatCount, formatStreakDate, isMonthBefore, shiftMonth, toDayString, } from '../utils';
import { isWeb } from '../../../constants';
const strings = {
    title: 'Streaks',
    dayInARow: 'day in a row with Emma',
    daysInARow: 'days in a row with Emma',
    yourRecords: 'Your records',
    inARow: 'in a row',
    inTotal: 'in total',
    since: 'since',
    stayActive: 'Stay active',
    tryAgain: 'Try again',
};
const BADGES_PER_ROW = 3;
// A failed request leaves neither, so the month is fetched again
const isLoadedOrLoading = (month) => Boolean(month?.activeDays || month?.isLoading);
const RecordCard = ({ value, description, footnote }) => (<CardView noMargin style={staticStyles.recordCard}>
    <View style={staticStyles.recordText}>
      <Text Text-20>{formatCount(value)}</Text>
      <Text TextThin-14>{description}</Text>
    </View>
    {footnote ? (<Text TextThin-12 Secondary>
        {footnote}
      </Text>) : null}
  </CardView>);
const StreaksScreen = withAnimatedNavigationHeader(({ onScroll }) => {
    const dispatch = useAppDispatch();
    // Read once per visit: the calendar and records compare against this day
    const [today] = useState(() => toDayString(new Date()));
    const currentMonth = today.slice(0, 7);
    const [month, setMonth] = useState(currentMonth);
    const summary = useAppSelector(selectStreaksSummary);
    const monthData = useAppSelector((store) => selectStreaksMonth(store, month));
    const badges = useAppSelector(selectStreakBadges);
    const userDayStreak = useAppSelector(selectUserDayStreak);
    useEffect(() => {
        Amplitude.logEvent('Streaks.Opened');
    }, []);
    const hasMonthData = isLoadedOrLoading(monthData);
    useEffect(() => {
        // Past months do not change, so fetch each once; always refresh this one
        if (!hasMonthData || month === currentMonth) {
            dispatch(getStreaks(month));
        }
    }, [month]);
    const currentStreak = summary?.currentStreak ?? userDayStreak;
    const firstOpenedOn = summary?.firstOpenedOn;
    // The total can lag behind the streaks until the backend backfills it
    const totalDaysOpened = summary
        ? Math.max(summary.totalDaysOpened, summary.longestStreak.days, summary.currentStreak)
        : 0;
    const previousMonth = shiftMonth(month, -1);
    const canGoBack = firstOpenedOn
        ? !isMonthBefore(previousMonth, firstOpenedOn)
        : false;
    const canGoForward = month < currentMonth;
    const previousMonthData = useAppSelector((store) => selectStreaksMonth(store, previousMonth));
    const nextMonthData = useAppSelector((store) => selectStreaksMonth(store, shiftMonth(month, 1)));
    const hasPreviousMonthData = isLoadedOrLoading(previousMonthData);
    // Days of the adjacent months, when loaded, show whether a streak carries
    // on past the start or end of this one
    const calendarActiveDays = useMemo(() => monthData?.activeDays && [
        ...(previousMonthData?.activeDays ?? []),
        ...monthData.activeDays,
        ...(nextMonthData?.activeDays ?? []),
    ], [
        previousMonthData?.activeDays,
        monthData?.activeDays,
        nextMonthData?.activeDays,
    ]);
    // Load the two months before the one shown. Going back then rarely waits,
    // and the month it lands on already has its own previous month's days
    const monthBeforePrevious = shiftMonth(month, -2);
    const canGoBackTwice = firstOpenedOn
        ? !isMonthBefore(monthBeforePrevious, firstOpenedOn)
        : false;
    const hasMonthBeforePreviousData = useAppSelector((store) => isLoadedOrLoading(selectStreaksMonth(store, monthBeforePrevious)));
    useEffect(() => {
        if (canGoBack && !hasPreviousMonthData) {
            dispatch(getStreaks(previousMonth));
        }
        if (canGoBackTwice && !hasMonthBeforePreviousData) {
            dispatch(getStreaks(monthBeforePrevious));
        }
    }, [previousMonth, canGoBack, canGoBackTwice]);
    // A failed request stops the loaders and shows what the store has
    const isMonthLoading = !monthData?.activeDays && monthData?.isLoading !== false;
    const isFirstLoad = !summary && isMonthLoading;
    // Only the first load blocks the screen. A past month that fails still
    // shows its calendar, without active days, so the user can move on
    const hasFailed = !summary &&
        Boolean(monthData) &&
        !monthData?.activeDays &&
        !monthData?.isLoading;
    // Keeps the error up while a retry runs, rather than flashing the loaders
    const [isRetrying, setIsRetrying] = useState(false);
    useEffect(() => {
        if (!monthData?.isLoading) {
            setIsRetrying(false);
        }
    }, [monthData?.isLoading]);
    const onTryAgain = useCallback(() => {
        setIsRetrying(true);
        dispatch(getStreaks(month));
    }, [month]);
    const onPressPrevious = useCallback(() => setMonth((value) => shiftMonth(value, -1)), []);
    const onPressNext = useCallback(() => setMonth((value) => shiftMonth(value, 1)), []);
    const unlockedCount = badges.filter((badge) => badge.isUnlocked).length;
    const badgeRows = useMemo(() => Array.from({ length: Math.ceil(badges.length / BADGES_PER_ROW) }, (_, index) => badges.slice(index * BADGES_PER_ROW, (index + 1) * BADGES_PER_ROW)), [badges]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottomStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    if (hasFailed || isRetrying) {
        return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <View testID="streaks-error">
            <ConnectionFailed buttonTitle={strings.tryAgain} withContactSupport={false} requesting={isRetrying} handleTryAgain={onTryAgain}/>
          </View>
        </AppView>);
    }
    if (isFirstLoad) {
        return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <View style={staticStyles.loader} testID="streaks-loader">
            <Indicator dark includeHeaderHeight/>
          </View>
        </AppView>);
    }
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <ScrollView onScroll={onScroll} scrollEventThrottle={16} contentContainerStyle={paddingBottomStyle}>
          <View style={staticStyles.header}>
            <Image source={require('../../reports/images/purple-flame.png')} style={staticStyles.flame} resizeMode="contain"/>
            <Text Text-32 centered testID="streaks-current">
              {formatCount(currentStreak)}
            </Text>
            <Text TextThin-14 Secondary centered>
              {currentStreak === 1 ? strings.dayInARow : strings.daysInARow}
            </Text>
          </View>

          <View style={staticStyles.section}>
            <StreaksCalendar month={month} today={today} activeDays={calendarActiveDays} isLoading={isMonthLoading} canGoBack={canGoBack} canGoForward={canGoForward} onPressPrevious={onPressPrevious} onPressNext={onPressNext}/>
          </View>

          {summary ? (<>
              <View style={staticStyles.sectionHeader}>
                <Text Text-16 Secondary>
                  {strings.yourRecords}
                </Text>
              </View>
              <View style={[staticStyles.section, staticStyles.row]}>
                <RecordCard value={summary.longestStreak.days} description={`${dayWord(summary.longestStreak.days)} ${strings.inARow}`} footnote={summary.longestStreak.endedOn
                ? formatStreakDate(summary.longestStreak.endedOn)
                : undefined}/>
                <RecordCard value={totalDaysOpened} description={`${dayWord(totalDaysOpened)} ${strings.inTotal}`} footnote={firstOpenedOn
                ? `${strings.since} ${formatStreakDate(firstOpenedOn)}`
                : undefined}/>
              </View>
            </>) : null}

          <View style={staticStyles.sectionHeader}>
            <Text Text-16 Secondary>
              {strings.stayActive}
            </Text>
            {/* Primary is the brand colour */}
            <Text Text-16 Primary testID="streak-badges-count">
              {`${unlockedCount}/${badges.length}`}
            </Text>
          </View>
          <View style={[staticStyles.section, staticStyles.badges]}>
            {badgeRows.map((row) => (<View key={row[0].days} style={staticStyles.row}>
                {row.map((badge) => (<StreakBadgeCard key={badge.days} badge={badge}/>))}
              </View>))}
          </View>
        </ScrollView>
      </AppView>);
}, ({ colors }) => ({
    title: strings.title,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const staticStyles = StyleSheet.create({
    loader: {
        flex: 1,
    },
    header: {
        alignItems: 'center',
        gap: rem(8),
        padding: rem(16),
    },
    flame: {
        width: rem(80),
        height: rem(80),
    },
    section: {
        paddingHorizontal: rem(16),
        paddingVertical: rem(8),
    },
    sectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: rem(16),
        paddingTop: rem(16),
        paddingBottom: rem(8),
    },
    row: {
        flexDirection: 'row',
        gap: rem(8),
    },
    badges: {
        gap: rem(8),
    },
    recordCard: {
        flex: 1,
        padding: rem(16),
        gap: rem(8),
    },
    recordText: {
        gap: rem(2),
    },
});
export default StreaksScreen;
